import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Clock } from 'lucide-react';
import {
  groupInstancesByJobSiteAndJob,
  groupTimesheetsByJobSiteAndJob,
  countJobSiteInstances,
  getColorScheme
} from '../../../utils/jobGrouping';
import JobSiteGroupHeader from './JobSiteGroupHeader';
import JobGroupRow from './JobGroupRow';
import InstanceRow from './InstanceRow';
import SingleJobRow from './SingleJobRow';

/**
 * ContractorJobHierarchy
 *
 * Composed component that renders the full 3-level hierarchy:
 * Job Site → Job → Instance
 *
 * Note: Contractors see jobs grouped by Job Site only, not Client.
 * Clients are a corporate-only concept.
 *
 * Supports multiple modes for different pages:
 * - 'today': Contractor's jobs for today with clock in/out actions
 * - 'upcoming': Contractor's upcoming jobs
 * - 'earnings': Contractor's earnings with timesheet data
 * - 'corporate-reports': Corporate reports page with approve/reject/dispute actions
 */
const ContractorJobHierarchy = ({
  instances = [],
  timesheets = [],
  onClockIn,
  onClockOut,
  onViewJobInvoice,
  onViewInstanceInvoice,
  onViewReport,
  // Contractor payment confirmation (for earnings mode)
  onConfirmPayment,
  isConfirmingPayment = false,
  // View payment proof (for earnings mode)
  onViewPaymentProof,
  // Corporate-reports mode actions
  onApprove,
  onReject,
  onDispute,
  onResolveDispute,
  onMarkPaid,
  selectedInstances = new Set(),
  onSelectInstance,
  mode = 'today', // 'today' | 'upcoming' | 'earnings' | 'corporate-reports'
  condensed = false,
  maxJobSites = null,
  highlightInstanceId = null
}) => {
  // Expansion state
  const [expandedJobSites, setExpandedJobSites] = useState(new Set());
  const [expandedJobs, setExpandedJobs] = useState(new Set());

  // Track if initial auto-expand has been done
  const initialExpandDone = useRef(false);
  const todayModeExpandDone = useRef(false);

  // Memoize grouped data to prevent unnecessary recalculations
  const groupedData = useMemo(() => {
    return mode === 'earnings'
      ? groupTimesheetsByJobSiteAndJob(timesheets)
      : groupInstancesByJobSiteAndJob(instances);
  }, [mode, instances, timesheets]);

  // Initial auto-expand for condensed mode (run once)
  useEffect(() => {
    if (initialExpandDone.current || !groupedData.size || !condensed) return;

    const jobSiteIds = [...groupedData.keys()];
    if (jobSiteIds.length > 0) {
      setExpandedJobSites(new Set([jobSiteIds[0]]));
      initialExpandDone.current = true;
    }
  }, [groupedData, condensed]);

  // Auto-expand all job sites and jobs for 'today' mode (run once)
  // This ensures contractors can see and clock into all their scheduled jobs
  useEffect(() => {
    if (todayModeExpandDone.current || mode !== 'today' || !groupedData.size) return;

    // Expand all job sites
    const allJobSiteIds = [...groupedData.keys()];
    setExpandedJobSites(new Set(allJobSiteIds));

    // Expand all jobs within each job site
    const allJobIds = [];
    for (const [, jobSiteGroup] of groupedData.entries()) {
      for (const jobId of jobSiteGroup.jobs.keys()) {
        allJobIds.push(jobId);
      }
    }
    setExpandedJobs(new Set(allJobIds));
    todayModeExpandDone.current = true;
  }, [mode, groupedData]);

  // Separate effect for highlight-based expansion
  useEffect(() => {
    if (!highlightInstanceId || !groupedData.size) return;

    const data = mode === 'earnings' ? timesheets : instances;
    const targetItem = data.find(item => {
      const instanceId = item?.id || item?.job_instance?.id;
      return instanceId === highlightInstanceId;
    });

    if (targetItem) {
      // Find which job site and job contains this instance
      for (const [jobSiteId, jobSiteGroup] of groupedData.entries()) {
        for (const [jobId, jobGroup] of jobSiteGroup.jobs.entries()) {
          const items = jobGroup.instances || jobGroup.timesheets || [];
          const found = items.some(item => {
            const id = item?.id || item?.job_instance?.id;
            return id === highlightInstanceId;
          });

          if (found) {
            setExpandedJobSites(prev => new Set([...prev, jobSiteId]));
            setExpandedJobs(prev => new Set([...prev, jobId]));

            // Scroll to element after a short delay
            setTimeout(() => {
              const element = document.getElementById(`job-card-${highlightInstanceId}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 300);

            return;
          }
        }
      }
    }
  }, [highlightInstanceId, groupedData, mode, instances, timesheets]);

  // Toggle handlers
  const toggleJobSite = useCallback((jobSiteId) => {
    setExpandedJobSites(prev => {
      const next = new Set(prev);
      if (next.has(jobSiteId)) {
        next.delete(jobSiteId);
      } else {
        next.add(jobSiteId);
      }
      return next;
    });
  }, []);

  const toggleJob = useCallback((jobId) => {
    setExpandedJobs(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });
  }, []);

  // Determine which features are active based on mode
  const showActions = mode === 'today' || mode === 'in_progress';
  const showDate = mode === 'upcoming' || mode === 'corporate-reports';
  const showEarnings = mode === 'earnings' || mode === 'corporate-reports';
  const showCorporateActions = mode === 'corporate-reports';

  // Apply max job sites limit if condensed
  let displayJobSites = [...groupedData.entries()];
  if (condensed && maxJobSites && displayJobSites.length > maxJobSites) {
    displayJobSites = displayJobSites.slice(0, maxJobSites);
  }

  // Empty state
  if (!displayJobSites.length) {
    const emptyMessages = {
      today: { title: 'No Jobs Scheduled Today', subtitle: 'Check back tomorrow for new assignments' },
      upcoming: { title: 'No Upcoming Jobs', subtitle: 'Your future schedule is clear' },
      earnings: { title: 'No Earnings Yet', subtitle: 'Complete jobs to start earning' },
      'corporate-reports': { title: 'No Reports Found', subtitle: 'Reports will appear here once contractors submit their work' }
    };

    const message = emptyMessages[mode] || emptyMessages.today;

    return (
      <div className="bg-surface border border-border rounded-xl p-12 text-center">
        <div className="w-20 h-20 bg-background rounded-full flex items-center justify-center mx-auto mb-6">
          <Clock size={40} className="text-text-secondary" />
        </div>
        <h3 className="text-xl font-semibold text-text-primary mb-2">{message.title}</h3>
        <p className="text-text-secondary">{message.subtitle}</p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {displayJobSites.map(([jobSiteId, jobSiteGroup], jobSiteIndex) => {
        const colorScheme = getColorScheme(jobSiteIndex);
        const isJobSiteExpanded = expandedJobSites.has(jobSiteId);
        const jobEntries = [...jobSiteGroup.jobs.entries()];
        const instanceCount = countJobSiteInstances(jobSiteGroup);

        return (
          <div key={jobSiteId}>
            {/* Job Site Header */}
            <JobSiteGroupHeader
              jobSite={jobSiteGroup.jobSite}
              jobCount={jobEntries.length}
              instanceCount={instanceCount}
              isExpanded={isJobSiteExpanded}
              onToggle={() => toggleJobSite(jobSiteId)}
              colorScheme={colorScheme}
              isFirst={jobSiteIndex === 0}
            />

            {/* Jobs within Job Site */}
            {isJobSiteExpanded && (
              <div>
                {jobEntries.map(([jobId, jobGroup], jobIndex) => {
                  const job = jobGroup.job;
                  const items = jobGroup.instances || jobGroup.timesheets || [];
                  const isJobExpanded = expandedJobs.has(jobId);
                  const isLastJob = jobIndex === jobEntries.length - 1;
                  const hasMultipleItems = items.length > 1;
                  const isSingleItem = items.length === 1;

                  // For earnings mode with single-instance jobs, use consolidated SingleJobRow
                  if (mode === 'earnings' && isSingleItem) {
                    const item = items[0];
                    const instance = item.job_instance;
                    const timesheet = item;
                    const instanceId = instance?.id || item?.id;
                    const isHighlighted = highlightInstanceId === instanceId;

                    return (
                      <SingleJobRow
                        key={jobId}
                        job={job}
                        instance={instance}
                        timesheet={timesheet}
                        colorScheme={colorScheme}
                        isLast={isLastJob}
                        isHighlighted={isHighlighted}
                        showInvoice={!!onViewJobInvoice}
                        onViewInvoice={onViewJobInvoice ? () => onViewJobInvoice(job, items) : undefined}
                        onConfirmPayment={onConfirmPayment}
                        isConfirmingPayment={isConfirmingPayment}
                        onViewPaymentProof={onViewPaymentProof}
                      />
                    );
                  }

                  return (
                    <div key={jobId}>
                      {/* Job Row */}
                      <JobGroupRow
                        job={job}
                        instanceCount={items.length}
                        isExpanded={isJobExpanded}
                        onToggle={() => toggleJob(jobId)}
                        colorScheme={colorScheme}
                        isLast={isLastJob && !isJobExpanded}
                        showChevron={hasMultipleItems}
                        showInvoice={(showEarnings || showCorporateActions) && !!onViewJobInvoice}
                        onViewInvoice={onViewJobInvoice ? () => {
                          // In earnings mode, pass full items (timesheets with nested job_instance)
                          // to preserve clock_in_time and other timesheet data
                          // In other modes, pass just the instances
                          onViewJobInvoice(job, items);
                        } : undefined}
                      />

                      {/* Instances within Job */}
                      {(isJobExpanded || !hasMultipleItems) && (
                        <div>
                          {items.map((item, instanceIndex) => {
                            const instance = mode === 'earnings'
                              ? item.job_instance
                              : item;
                            // For corporate-reports, timesheet is nested in the item
                            // For earnings mode, the item IS the timesheet
                            const timesheet = mode === 'earnings'
                              ? item
                              : (mode === 'corporate-reports' ? (item?.timesheet || item?.timesheet?.[0]) : null);
                            const instanceId = instance?.id || item?.id;
                            const isLastInstance = instanceIndex === items.length - 1;
                            const isHighlighted = highlightInstanceId === instanceId;

                            return (
                              <InstanceRow
                                key={instanceId || instanceIndex}
                                instance={instance}
                                job={job}
                                colorScheme={colorScheme}
                                isLast={isLastInstance && isLastJob}
                                instanceNumber={instance?.instance_number || instanceIndex + 1}
                                onClockIn={onClockIn}
                                onClockOut={onClockOut}
                                showActions={showActions}
                                showDate={showDate}
                                showEarnings={showEarnings}
                                timesheet={timesheet}
                                isHighlighted={isHighlighted}
                                showInvoice={(showEarnings || showCorporateActions) && !!onViewInstanceInvoice}
                                onViewInvoice={onViewInstanceInvoice ? () => onViewInstanceInvoice(job, instance, timesheet) : undefined}
                                // Contractor payment confirmation props (for earnings mode)
                                onConfirmPayment={mode === 'earnings' ? onConfirmPayment : undefined}
                                isConfirmingPayment={isConfirmingPayment}
                                // View payment proof (for earnings mode)
                                onViewPaymentProof={mode === 'earnings' ? onViewPaymentProof : undefined}
                                // Corporate-reports mode props
                                showCorporateActions={showCorporateActions}
                                isSelected={selectedInstances.has(instanceId)}
                                onSelect={onSelectInstance ? (selected) => onSelectInstance(instanceId, selected) : undefined}
                                onViewReport={onViewReport ? () => onViewReport(item) : undefined}
                                onApprove={onApprove ? () => onApprove(instanceId) : undefined}
                                onReject={onReject ? () => onReject(item) : undefined}
                                onDispute={onDispute ? () => onDispute(item) : undefined}
                                onResolveDispute={onResolveDispute ? (resolution) => onResolveDispute(instanceId, resolution) : undefined}
                                onMarkPaid={onMarkPaid ? () => onMarkPaid(instanceId) : undefined}
                                contractor={item?.contractor}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ContractorJobHierarchy;
